import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { languages } from '../../i18n';
import { Link } from 'react-router-dom';

export const Footer = () => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lng: string) => {
    changeLanguage(lng);
    setShowLanguageDropdown(false);
  };

  // Get current language display name
  const getCurrentLanguageDisplay = () => {
    return languages[currentLanguage as keyof typeof languages]?.nativeName || currentLanguage.toUpperCase();
  };
  
  return (
    <footer className="bg-white mt-6 mb-16 py-6 px-5 border-t border-gray-100">
      <div className="flex flex-col items-start space-y-4">
        {/* Language Switcher */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
            className="flex items-center gap-2 bg-white border-none px-0.5 py-0.5"
          >
            <Globe className="h-4 w-4 text-black" />
            <span className="text-sm text-black">{getCurrentLanguageDisplay()}</span>
          </button>
          
          {showLanguageDropdown && (
            <div className="absolute bottom-full mb-4 left-0 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="py-2">
                {Object.keys(languages).map((lng) => {
                  const isSelected = currentLanguage === lng;
                  return (
                    <button
                      key={lng}
                      onClick={() => handleLanguageChange(lng)}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between border-none ${
                        isSelected ? 'bg-white text-black' : 'bg-white text-gray-700'
                      }`}
                    >
                      <span>{languages[lng as keyof typeof languages].nativeName}</span>
                      {isSelected && (
                        <div className="w-2 h-2 bg-black rounded-full"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Copyright */}
        <p className="text-center text-xs text-gray-600">
          &copy; {new Date().getFullYear()} {t('flowers.footer.copyright')}
        </p>
        
        {/* Links */}
        <div className="flex space-x-4">
          <Link to="/delivery-range" className="text-xs text-gray-700 hover:text-gray-900">{t('flowers.footer.serviceArea')}</Link>
          <a href="/terms" className="text-xs text-gray-700 hover:text-gray-900">{t('flowers.footer.terms')}</a>
          <a href="/privacy" className="text-xs text-gray-700 hover:text-gray-900">{t('flowers.footer.privacy')}</a>
          <a href="/contact" className="text-xs text-gray-700 hover:text-gray-900">{t('flowers.footer.contact')}</a>
        </div>
      </div>
    </footer>
  );
};  